import React from 'react';
import { APP_STORE_LINKS } from '../../../config/appLinks';

/**
 * High-fidelity App Store and Google Play badges
 * 
 * @param {Object} props
 * @param {'dark' | 'light' | 'glass'} [props.variant='dark']
 * @param {'normal' | 'large' | 'compact'} [props.size='normal']
 * @param {'row' | 'col'} [props.direction='row']
 * @param {string} [props.className='']
 * @param {boolean} [props.showAppStore=true]
 * @param {boolean} [props.showPlayStore=true]
 */
const AppStoreBadges = ({
  variant = 'dark',
  size = 'normal',
  direction = 'row',
  className = '',
  showAppStore = true,
  showPlayStore = true,
}) => {
  // Styles based on variant
  const getVariantStyles = () => {
    switch (variant) {
      case 'light':
        return 'bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 hover:border-slate-300 shadow-sm';
      case 'glass':
        return 'bg-white/10 text-white backdrop-blur-md border border-white/20 hover:bg-white/20 shadow-lg';
      case 'dark':
      default:
        return 'bg-slate-900 text-white border border-slate-700/80 hover:bg-slate-800 hover:border-slate-600 shadow-md';
    }
  };

  // Sizing styles
  const getSizeStyles = () => {
    switch (size) {
      case 'large':
        return {
          btn: 'px-5 py-3 rounded-2xl gap-3 text-left',
          icon: 'w-7 h-7',
          subtext: 'text-xs md:text-[10px] tracking-wider uppercase leading-none',
          title: 'text-base font-bold leading-tight mt-0.5',
        };
      case 'compact':
        return {
          btn: 'px-3 py-1.5 rounded-xl gap-2 text-left',
          icon: 'w-5 h-5',
          subtext: 'text-xs md:text-[9px] tracking-wider uppercase leading-none',
          title: 'text-xs font-bold leading-tight mt-0.5',
        };
      case 'normal':
      default:
        return {
          btn: 'px-4 py-2.5 rounded-xl gap-3 text-left',
          icon: 'w-6 h-6',
          subtext: 'text-xs md:text-[10px] tracking-wider uppercase leading-none',
          title: 'text-base md:text-sm font-bold leading-tight mt-0.5',
        };
    }
  };

  const sz = getSizeStyles();
  const baseBtn = `inline-flex items-center min-h-12 md:min-h-0 min-w-12 md:min-w-0 transition-all duration-200 group active:scale-[0.98] ${sz.btn} ${getVariantStyles()}`;

  return (
    <div
      className={`flex ${
        direction === 'col' ? 'flex-col space-y-3' : 'flex-wrap items-center gap-3'
      } ${className}`}
    >
      {/* Apple App Store */}
      {showAppStore && (
        <a
          href={APP_STORE_LINKS.APPLE_APP_STORE}
          target="_blank"
          rel="noopener noreferrer"
          className={baseBtn}
          aria-label="Download on the App Store"
        >
          {/* Apple SVG Logo */}
          <svg
            className={`${sz.icon} flex-shrink-0 transition-transform group-hover:scale-105`}
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.61-.75 1.04-1.8 0.92-2.85-.92.04-2.02.62-2.67 1.38-.56.65-1.06 1.71-.93 2.73 1.03.08 2.07-.51 2.68-1.26z" />
          </svg>
          <div className="flex flex-col">
            <span className={sz.subtext}>Download on the</span>
            {' '}
            <span className={sz.title}>App Store</span>
          </div>
        </a>
      )}

      {/* Google Play Store */}
      {showPlayStore && (
        <a
          href={APP_STORE_LINKS.GOOGLE_PLAY_STORE}
          target="_blank"
          rel="noopener noreferrer"
          className={baseBtn}
          aria-label="Get it on Google Play"
        >
          {/* Google Play SVG Logo */}
          <svg
            className={`${sz.icon} flex-shrink-0 transition-transform group-hover:scale-105`}
            viewBox="0 0 24 24"
          >
            <path
              fill="#4285F4"
              d="M3.609 1.813L13.793 12 3.61 22.187C3.25 21.758 3 21.052 3 20.082V3.918c0-.97.25-1.676.609-2.105z"
            />
            <path
              fill="#FBBC04"
              d="M17.18 8.613L13.793 12l3.387 3.387 3.844-2.203c1.094-.625 1.094-1.656 0-2.281l-3.844-2.29z"
            />
            <path
              fill="#34A853"
              d="M3.609 22.187L13.793 12l3.387 3.387-10.422 5.969c-.89.515-1.953.406-2.75-.414l6.001-8.756z"
            />
            <path
              fill="#EA4335"
              d="M3.609 1.813l13.571 7.781-3.387 3.406L3.609 1.813c.797-.82 1.86-.929 2.75-.414z"
            />
          </svg>
          <div className="flex flex-col">
            <span className={sz.subtext}>GET IT ON</span>
            {' '}
            <span className={sz.title}>Google Play</span>
          </div>
        </a>
      )}
    </div>
  );
};

export default AppStoreBadges;
